import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import {
  getReglements,
  deleteReglement,
  updateReglement,
} from "../../../../../services/achatsServices";
import { getSettings } from "../../../../../services/settingsServices";
import { getArticles } from "../../../../../services/articlesServices";
import { getAllUsers } from "../../../../../services/userServices";
import { sendReminderMail } from "../../../../../services/mailServices";

import {
  DEFAULT_PAYMENT_FILTERS,
  PAYMENT_STATUS,
  calculatePaymentsStats,
  canSendReminder,
  filterPayments,
  formatDateFR,
  formatEuro,
  getArticleName,
  getArticleOptions,
  getUniqueSeasons,
  getUserFullName,
  isWithinSeason,
  safeArray,
  getReminderCount,
  getReminderHistory,
} from "../utils/paymentsDashboardUtils";

function unwrapSettings(settingsData) {
  if (Array.isArray(settingsData)) return settingsData[0] || {};
  return settingsData || {};
}

export function usePaymentsDashboard() {
  const initialSeasonAppliedRef = useRef(false);

  const [payments, setPayments] = useState([]);
  const [articles, setArticles] = useState([]);
  const [users, setUsers] = useState([]);
  const [adherentsByUser, setAdherentsByUser] = useState({});
  const [activeSeason, setActiveSeason] = useState("");
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(DEFAULT_PAYMENT_FILTERS);

  const [helpOpen, setHelpOpen] = useState(false);

  const [createBoutiqueModalOpen, setCreateBoutiqueModalOpen] = useState(false);
  const [createSpecialModalOpen, setCreateSpecialModalOpen] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editablePayment, setEditablePayment] = useState(null);

  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [detailsPayment, setDetailsPayment] = useState(null);

  const [reminderHistoryDrawerOpen, setReminderHistoryDrawerOpen] =
    useState(false);
  const [reminderHistoryPayment, setReminderHistoryPayment] = useState(null);

  const [deletingId, setDeletingId] = useState(null);
  const [remindingId, setRemindingId] = useState(null);
  const [togglingAuthorizationId, setTogglingAuthorizationId] = useState(null);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);

      const [paymentsData, settingsData, articlesData, usersData] =
        await Promise.all([
          getReglements(),
          getSettings(),
          getArticles(),
          getAllUsers(),
        ]);

      const rows = safeArray(paymentsData);
      const allUsers = safeArray(usersData);
      const settings = unwrapSettings(settingsData);
      const season = settings?.saison_active || "";

      const byUser = {};
      allUsers.forEach((user) => {
        byUser[user.id] = safeArray(user.adherents);
      });

      setPayments(rows);
      setArticles(safeArray(articlesData));
      setUsers(allUsers);
      setAdherentsByUser(byUser);
      setActiveSeason(season);

      if (!initialSeasonAppliedRef.current) {
        initialSeasonAppliedRef.current = true;

        setFilters((prev) => ({
          ...prev,
          season: prev.season || season || "",
        }));
      }
    } catch (error) {
      console.error("Erreur chargement paiements :", error);
      toast.error("Erreur lors du chargement des paiements.");
      setPayments([]);
      setArticles([]);
      setUsers([]);
      setAdherentsByUser({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const articleOptions = useMemo(() => getArticleOptions(articles), [articles]);

  const seasons = useMemo(
    () => getUniqueSeasons(payments, activeSeason),
    [payments, activeSeason],
  );

  const filteredPayments = useMemo(() => {
    return filterPayments(payments, filters, adherentsByUser);
  }, [payments, filters, adherentsByUser]);

  const selectedSeason = filters.season || activeSeason || "";

  const seasonPayments = useMemo(() => {
    if (!selectedSeason) return payments;

    return payments.filter((payment) =>
      isWithinSeason(payment?.date_achat, selectedSeason),
    );
  }, [payments, selectedSeason]);

  const stats = useMemo(
    () => calculatePaymentsStats(seasonPayments),
    [seasonPayments],
  );

  const filteredStats = useMemo(
    () => calculatePaymentsStats(filteredPayments),
    [filteredPayments],
  );

  const hasActiveFilters = useMemo(() => {
    return (
      filters.query.trim() !== "" ||
      filters.article !== "" ||
      filters.statuses.length > 0 ||
      filters.type !== "" ||
      filters.kind !== "" ||
      filters.authorization !== "" ||
      (filters.season !== "" && filters.season !== activeSeason)
    );
  }, [filters, activeSeason]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleStatusFilter = (status) => {
    setFilters((prev) => {
      const current = safeArray(prev.statuses);

      return {
        ...prev,
        statuses: current.includes(status)
          ? current.filter((item) => item !== status)
          : [...current, status],
      };
    });
  };

  const selectAllStatuses = () => {
    setFilters((prev) => ({
      ...prev,
      statuses: [...PAYMENT_STATUS],
    }));
  };

  const clearStatusFilters = () => {
    setFilters((prev) => ({
      ...prev,
      statuses: [],
    }));
  };

  const resetFilters = () => {
    setFilters({
      ...DEFAULT_PAYMENT_FILTERS,
      season: activeSeason || "",
    });
  };

  const toggleHelp = () => setHelpOpen((prev) => !prev);

  const openCreateBoutiqueModal = () => setCreateBoutiqueModalOpen(true);
  const closeCreateBoutiqueModal = () => setCreateBoutiqueModalOpen(false);

  const openCreateSpecialModal = () => setCreateSpecialModalOpen(true);
  const closeCreateSpecialModal = () => setCreateSpecialModalOpen(false);

  const openEditModal = (payment) => {
    setEditablePayment(payment || null);
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditablePayment(null);
  };

  const openDetailsDrawer = (payment) => {
    setDetailsPayment(payment || null);
    setDetailsDrawerOpen(true);
  };

  const closeDetailsDrawer = () => {
    setDetailsDrawerOpen(false);
    setDetailsPayment(null);
  };

  const openReminderHistoryDrawer = (payment) => {
    setReminderHistoryPayment(payment || null);
    setReminderHistoryDrawerOpen(true);
  };

  const closeReminderHistoryDrawer = () => {
    setReminderHistoryDrawerOpen(false);
    setReminderHistoryPayment(null);
  };

  const deletePayment = async (paymentOrId) => {
    const payment =
      typeof paymentOrId === "object"
        ? paymentOrId
        : payments.find((item) => item.id === paymentOrId);

    const id = typeof paymentOrId === "object" ? paymentOrId?.id : paymentOrId;

    if (!id) return;

    const label = payment
      ? `${getArticleName(payment)} — ${getUserFullName(payment.User)}`
      : "ce règlement";

    if (!window.confirm(`Supprimer définitivement « ${label} » ?`)) {
      return;
    }

    try {
      setDeletingId(id);

      await deleteReglement(id);

      toast.success("Paiement supprimé avec succès.");

      if (detailsPayment?.id === id) {
        closeDetailsDrawer();
      }

      await fetchPayments();
    } catch (error) {
      console.error("Erreur suppression paiement :", error);
      toast.error("Erreur lors de la suppression du paiement.");
    } finally {
      setDeletingId(null);
    }
  };

  const sendReminder = async (payment) => {
    if (!payment?.id) return;

    if (!canSendReminder(payment)) {
      toast.warn("Aucun rappel possible pour ce paiement.");
      return;
    }

    const email = payment.User?.email;
    const userName = getUserFullName(payment.User);
    const article = getArticleName(payment);
    const amount = formatEuro(payment.prix_total);
    const date = formatDateFR(payment.date_achat);

    const subject = "🔔 Rappel de paiement en attente";

    const text = `Bonjour ${userName},

Nous vous rappelons que le paiement suivant est en attente :

- Article : ${article}
- Montant : ${amount}
- Date d'achat : ${date}

Merci de procéder au règlement ou de contacter le club pour toute question.
https://www.usdugny-judo.fr/commandes-&-reglements

Sportivement,
Le club`;

    try {
      setRemindingId(payment.id);

      const response = await sendReminderMail({
        achat_id: payment.id,
        email,
        subject,
        text,
      });

      const fallbackReminder = {
        sent_at: new Date().toISOString(),
        channel: "email",
        subject,
      };

      const nextReminderHistory = response?.rappels_paiement || [
        ...getReminderHistory(payment),
        fallbackReminder,
      ];

      const patch = {
        rappels_paiement: nextReminderHistory,
        dernier_rappel_paiement_at:
          response?.dernier_rappel_paiement_at || fallbackReminder.sent_at,
      };

      toast.success(
        `Mail de rappel envoyé. Total : ${
          response?.nb_rappels_paiement ??
          getReminderCount({ ...payment, ...patch })
        } rappel(s).`,
      );

      setPayments((prev) =>
        prev.map((item) =>
          item.id === payment.id
            ? {
                ...item,
                ...patch,
              }
            : item,
        ),
      );

      setDetailsPayment((prev) =>
        prev?.id === payment.id
          ? {
              ...prev,
              ...patch,
            }
          : prev,
      );

      setReminderHistoryPayment((prev) =>
        prev?.id === payment.id
          ? {
              ...prev,
              ...patch,
            }
          : prev,
      );
    } catch (error) {
      console.error("Erreur envoi rappel :", error);
      toast.error("Erreur lors de l’envoi du mail de rappel.");
    } finally {
      setRemindingId(null);
    }
  };

  const toggleAuthorization = async (payment) => {
    if (!payment?.id) return;

    try {
      setTogglingAuthorizationId(payment.id);

      await updateReglement(payment.id, {
        ...payment,
        autorisation_achat: !payment.autorisation_achat,
      });

      toast.success("Autorisation de paiement mise à jour.");

      setPayments((prev) =>
        prev.map((item) =>
          item.id === payment.id
            ? {
                ...item,
                autorisation_achat: !item.autorisation_achat,
              }
            : item,
        ),
      );

      if (detailsPayment?.id === payment.id) {
        setDetailsPayment((prev) =>
          prev
            ? {
                ...prev,
                autorisation_achat: !prev.autorisation_achat,
              }
            : prev,
        );
      }

      await fetchPayments();
    } catch (error) {
      console.error("Erreur autorisation paiement :", error);
      toast.error("Erreur lors de la mise à jour de l’autorisation.");
    } finally {
      setTogglingAuthorizationId(null);
    }
  };

  return {
    selectedSeason,
    seasonPayments,
    payments,
    articles,
    users,
    adherentsByUser,
    activeSeason,
    loading,

    filters,
    setFilter,
    resetFilters,
    toggleStatusFilter,
    selectAllStatuses,
    clearStatusFilters,
    hasActiveFilters,

    articleOptions,
    seasons,

    filteredPayments,
    stats,
    filteredStats,

    helpOpen,
    toggleHelp,

    createBoutiqueModalOpen,
    openCreateBoutiqueModal,
    closeCreateBoutiqueModal,

    createSpecialModalOpen,
    openCreateSpecialModal,
    closeCreateSpecialModal,

    editModalOpen,
    editablePayment,
    openEditModal,
    closeEditModal,

    detailsDrawerOpen,
    detailsPayment,
    openDetailsDrawer,
    closeDetailsDrawer,

    reminderHistoryDrawerOpen,
    reminderHistoryPayment,
    openReminderHistoryDrawer,
    closeReminderHistoryDrawer,

    deletingId,
    remindingId,
    togglingAuthorizationId,

    fetchPayments,
    deletePayment,
    sendReminder,
    toggleAuthorization,
  };
}
